import { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY, LOGO } from "@/lib/site";

const NAV = [
  { label: "Services", href: "#services" },
  { label: "Portfolio", href: "#portfolio" },
  { label: "About", href: "#about" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onLight = scrolled;
  const textCls = onLight ? "text-navy" : "text-white";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-xl border-b border-border elev-1" : "bg-transparent"
      }`}
    >
      <div className="px-6 md:px-10 lg:px-14 h-20 flex items-center justify-between">
        <a href="#top" data-testid="logo-link" className="flex items-center gap-3 group">
          <div
            className={`h-12 px-2 flex items-center rounded-lg transition-colors ${
              onLight ? "bg-transparent" : "bg-white/95 backdrop-blur-sm"
            }`}
          >
            <img
              src={LOGO.primary}
              alt="Five Peaks Construction LLC"
              className="h-9 w-auto object-contain"
            />
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={`text-sm tracking-wide font-medium hover:text-sunrise transition-colors ${textCls}`}
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href={COMPANY.phoneHref}
            data-testid="header-phone"
            className={`text-sm font-semibold flex items-center gap-2 hover:text-sunrise transition-colors ${textCls}`}
          >
            <Phone className="w-4 h-4" /> {COMPANY.phone}
          </a>
          <a
            href="#contact"
            data-testid="header-cta-quote"
            className="bg-sunrise hover:bg-sunrise-hover text-white px-5 py-3 text-sm font-semibold tracking-wide transition-colors rounded-lg shadow-sm"
          >
            Get a Free Quote
          </a>
        </div>

        <button
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          className={`lg:hidden p-2 ${textCls}`}
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-border">
          <nav className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className="text-navy font-medium hover:text-sunrise transition-colors"
              >
                {n.label}
              </a>
            ))}
            <a
              href={COMPANY.phoneHref}
              className="text-navy font-medium flex items-center gap-2"
            >
              <Phone className="w-4 h-4" /> {COMPANY.phone}
            </a>
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-cta-quote"
              className="bg-sunrise text-white px-5 py-3 text-sm font-semibold tracking-wide text-center rounded-lg"
            >
              Get a Free Quote
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
